/**
 * Where a user's dashboard control layout lives.
 *
 * API first: `GET /card-preferences/{family_tree_id}` to read and
 * `POST /save-card-preferences` to write. A preference is one row per card —
 * section ("top_cards" for the grid, "quick_actions" for the tray), card key,
 * display order and visibility. Every read still falls back to a per-user
 * localStorage copy and every save writes that copy first, so a failed request
 * leaves the layout working on this device instead of snapping back on the
 * next render.
 */

import { getApi, postApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";

const CARD_PREFERENCES_ENDPOINT = "/card-preferences";
const SAVE_CARD_PREFERENCES_ENDPOINT = "/save-card-preferences";
const LOCAL_KEY_PREFIX = "dashboard_control_layout";

/** The grid, and the tray of everything the user pushed off it. */
const SECTION_TOP_CARDS = "top_cards";
const SECTION_QUICK_ACTIONS = "quick_actions";

function getFamilyTreeId() {
  const raw = getFamilyTreeIdFromStorage();
  return raw != null && String(raw).trim() !== "" ? String(raw).trim() : "";
}

function getLoggedInUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("userDetail") || "{}");
    const id = user?.id ?? user?.user_id ?? user?._id;
    return id != null && String(id).trim() !== "" ? String(id).trim() : "";
  } catch (error) {
    return "";
  }
}

/** Scoped per user and per tree: switching either shows that context's layout. */
function getLocalStorageKey() {
  return `${LOCAL_KEY_PREFIX}:${getLoggedInUserId() || "anon"}:${
    getFamilyTreeId() || "none"
  }`;
}

/** Contexts already pushed to the backend this session — see seedDashboardControlLayout. */
const seededLayoutKeys = new Set();

/**
 * Card keys the backend is known to hold a row for, per context. Filled from
 * the GET and from every successful save, and consulted when posting a diff:
 * a card the server has never seen has to be sent in full even if it did not
 * move, or its record is never created.
 */
const serverKnownCards = new Map();

function getKnownServerCards() {
  return serverKnownCards.get(getLocalStorageKey()) || new Set();
}

function rememberServerCards(cardKeys) {
  const key = getLocalStorageKey();
  const known = serverKnownCards.get(key) || new Set();
  cardKeys.forEach((cardKey) => known.add(cardKey));
  serverKnownCards.set(key, known);
}

function readLocalLayout() {
  try {
    const raw = localStorage.getItem(getLocalStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.visibleIds)) return parsed.visibleIds;
    return null;
  } catch (error) {
    return null;
  }
}

function writeLocalLayout(visibleIds) {
  try {
    localStorage.setItem(
      getLocalStorageKey(),
      JSON.stringify({ visibleIds, updated_at: Date.now() })
    );
  } catch (error) {
    // Private mode / quota — the layout just won't survive a reload.
  }
}

/**
 * Rows → the ordered ids on the grid.
 *
 * Only `section_key: "top_cards"` rows lay out the grid; quick-action rows are
 * the leftovers, which the catalog already derives. A payload with no section
 * on its rows at all is read as one flat list rather than dropped.
 */
export function extractVisibleIdsFromCardPreferences(payload) {
  const root = payload?.data ?? payload;
  const list = [root, root?.card_preferences, root?.preferences].find((candidate) =>
    Array.isArray(candidate)
  );
  if (!list) return null;

  const hasSections = list.some(
    (entry) => entry && typeof entry === "object" && entry.section_key != null
  );

  const rows = [];
  list.forEach((entry, index) => {
    if (!entry || typeof entry !== "object") return;
    if (hasSections && entry.section_key !== SECTION_TOP_CARDS) return;
    if (entry.is_visible === false) return;

    const cardKey = entry.card_key;
    if (cardKey == null || String(cardKey).trim() === "") return;

    const order = Number(entry.display_order);
    rows.push({
      id: String(cardKey).trim(),
      order: Number.isFinite(order) ? order : index,
    });
  });

  rows.sort((a, b) => a.order - b.order);
  return rows.map((row) => row.id);
}

/** Every card_key in a preferences payload, both sections, visible or not. */
function collectCardKeys(payload) {
  const root = payload?.data ?? payload;
  const list = [root, root?.card_preferences, root?.preferences].find((candidate) =>
    Array.isArray(candidate)
  );
  if (!list) return [];
  return list
    .filter((entry) => entry && typeof entry === "object" && entry.card_key != null)
    .map((entry) => String(entry.card_key).trim())
    .filter(Boolean);
}

/**
 * @returns {Promise<{visibleIds: string[]|null, source: "api"|"local"|"default"}>}
 * `visibleIds` is null when nothing has ever been saved — the caller shows the
 * default layout rather than an empty dashboard.
 */
export async function fetchDashboardControlLayout() {
  const familyTreeId = getFamilyTreeId();

  if (familyTreeId) {
    try {
      const response = await getApi(
        `${CARD_PREFERENCES_ENDPOINT}/${encodeURIComponent(familyTreeId)}`
      );
      const visibleIds = extractVisibleIdsFromCardPreferences(response);
      rememberServerCards(collectCardKeys(response));
      // An empty list is "no preferences saved yet", not "hide everything".
      if (visibleIds && visibleIds.length) {
        writeLocalLayout(visibleIds);
        return { visibleIds, source: "api" };
      }
    } catch (error) {
      // Endpoint unreachable — fall through to the local copy.
    }
  }

  const local = readLocalLayout();
  return local
    ? { visibleIds: local, source: "local" }
    : { visibleIds: null, source: "default" };
}

/**
 * Both sections in one payload: the grid in the order the user arranged it,
 * then everything else as quick actions, so the server's picture matches the
 * dashboard's exactly instead of leaving dropped cards behind.
 */
function buildPreferenceRows(visibleIds, hiddenIds) {
  const rows = visibleIds.map((id, index) => ({
    section_key: SECTION_TOP_CARDS,
    card_key: id,
    display_order: index + 1,
    is_visible: true,
  }));
  hiddenIds.forEach((id, index) => {
    rows.push({
      section_key: SECTION_QUICK_ACTIONS,
      card_key: id,
      display_order: index + 1,
      is_visible: true,
    });
  });
  return rows;
}

/**
 * Only the rows the user actually moved. The endpoint upserts per card, so a
 * layout where one card was swapped out sends that card and the ones whose
 * position shifted behind it — not the whole board every time.
 *
 * A card with no previous row at all is new to this user and always goes.
 */
function diffPreferenceRows(nextRows, previousRows) {
  const before = new Map(previousRows.map((row) => [row.card_key, row]));
  const known = getKnownServerCards();
  return nextRows.filter((row) => {
    const previous = before.get(row.card_key);
    return (
      !known.has(row.card_key) ||
      !previous ||
      previous.section_key !== row.section_key ||
      previous.display_order !== row.display_order ||
      previous.is_visible !== row.is_visible
    );
  });
}

/**
 * @param {string[]} visibleIds cards on the grid, in order
 * @param {string[]} [hiddenIds] cards left in the Quick Actions tray
 * @param {{visibleIds: string[], hiddenIds: string[]}} [previousLayout] what
 * the backend already holds. Given, only the changed cards are posted; omitted
 * (a first save / seed), the full board is.
 * @returns {Promise<{source: "api"|"local"|"unchanged"}>} where the layout
 * actually landed, so the UI can tell the user it is only saved on this device.
 */
export async function saveDashboardControlLayout(
  visibleIds,
  hiddenIds = [],
  previousLayout = null
) {
  const ids = Array.isArray(visibleIds) ? visibleIds : [];
  const hidden = Array.isArray(hiddenIds) ? hiddenIds : [];
  writeLocalLayout(ids);

  const familyTreeId = getFamilyTreeId();
  if (!familyTreeId) return { source: "local" };

  const nextRows = buildPreferenceRows(ids, hidden);
  const cards = previousLayout
    ? diffPreferenceRows(
        nextRows,
        buildPreferenceRows(
          previousLayout.visibleIds || [],
          previousLayout.hiddenIds || []
        )
      )
    : nextRows;

  // Nothing moved — the local copy is already written, so there is no request
  // worth making.
  if (!cards.length) return { source: "unchanged" };

  try {
    await postApi(SAVE_CARD_PREFERENCES_ENDPOINT, {
      family_tree_id: familyTreeId,
      cards,
    });
    rememberServerCards(cards.map((row) => row.card_key));
    seededLayoutKeys.add(getLocalStorageKey());
    return { source: "api" };
  } catch (error) {
    return { source: "local" };
  }
}

/**
 * First run for this user and tree: the GET came back empty, so push the
 * layout the dashboard is already showing to the backend instead of waiting
 * for the user to open the editor. Attempted once per context per session —
 * the layout reload also fires on every family-tree switch.
 */
export async function seedDashboardControlLayout(visibleIds, hiddenIds = []) {
  if (!getFamilyTreeId()) return { source: "local" };
  const key = getLocalStorageKey();
  if (seededLayoutKeys.has(key)) return { source: "skipped" };
  seededLayoutKeys.add(key);

  const result = await saveDashboardControlLayout(visibleIds, hiddenIds);
  // Let a later reload retry a seed that never reached the server.
  if (result.source !== "api") seededLayoutKeys.delete(key);
  return result;
}

/**
 * Back to the catalog defaults. Saved explicitly rather than cleared so the
 * choice survives a reload the same way any other layout does.
 */
export async function resetDashboardControlLayout(
  defaultVisibleIds,
  hiddenIds = [],
  previousLayout = null
) {
  return saveDashboardControlLayout(defaultVisibleIds, hiddenIds, previousLayout);
}
